// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import type { NavLinkType } from '../types';
import GlobalNavApiBinding from '../GlobalNavApiBinding';
import VisibleLinks from './VisibleLinks';
import SideBarCollapsedMenu from './SideBarCollapsedMenu';
import SideBarNavItem from './SideBarNavItem';
import SideBarSettingsLinks from './SideBarSettingsLinks';

type ExternalProps = {|
  /** The navigation links to display in the side bar */
  links: ?(NavLinkType[]),
  /** Callback for expanding sidebar item */
  sidebarItemExpandHandler?: (subLinks: NavLinkType[], e: SyntheticEvent<*>) => void,
|};

type Props = {|
  ...ExternalProps,
  /** The settings links (settings/signout) only get displayed in the sidebar if the user is logged in */
  displaySettingsLinks: boolean,
|};

/** Filter out links that we do not actually want to display. If the link should not appear in the side bar filter it out. */
const filterUnusedLinks = (navLinks: NavLinkType[]): NavLinkType[] =>
  navLinks.filter(link => !link.hideFromSidebar);

/** Return an array of links that should collapse into the "More" toggle cell */
const getCollapsedLinks = (navLinks: NavLinkType[]): NavLinkType[] =>
  navLinks.filter(link => link.forceCollapse);

/** Return an array of links that should always be visible (not collapsed) that show before our collapsed menu */
const getPreCollapsedVisibleLinks = (navLinks: NavLinkType[]): NavLinkType[] =>
  navLinks.filter(link => !link.forceCollapse && !link.sidebarPostCollapsed);

/** Return an array of links that should always be visible (not collapsed) that show after our collapsed menu */
const getPostCollapsedVisibleLinks = (navLinks: NavLinkType[]): NavLinkType[] =>
  navLinks.filter(link => !link.forceCollapse && link.sidebarPostCollapsed);

/** A component that will render all of the navigation links in the side bar */
const SideBarNavLinks = ({ links, sidebarItemExpandHandler, displaySettingsLinks }: Props) => {
  if (links === null || typeof links === 'undefined') {
    return null;
  }
  const filteredLinks = filterUnusedLinks(links); // Get rid of unused/unwanted links that should not appear in the sidebar
  const visibleLinks = getPreCollapsedVisibleLinks(filteredLinks); // The visible links that come before our collapsed "More" section
  const collapsedLinks = getCollapsedLinks(filteredLinks); // Links that collapse into the "More" section
  const postCollapseLinks = getPostCollapsedVisibleLinks(filteredLinks); // Link(s) that appear after the collapsed "More" section. This array should end up being only the "Help Center" link.

  return (
    <React.Fragment>
      <VisibleLinks links={visibleLinks} expandLinks={sidebarItemExpandHandler} />
      <SideBarCollapsedMenu collapsedLinks={collapsedLinks} />
      <ul>
        {postCollapseLinks.map(link => (
          <SideBarNavItem key={`sbn_${link.id}_${link.text}`} link={link} />
        ))}
      </ul>
      {displaySettingsLinks ? <SideBarSettingsLinks /> : null}
    </React.Fragment>
  );
};

/** Connect SideBarNavLinks to the redux store so it can render decide whether or not to display the settings links based on the store's auth state, ownProps comes from GlobalNavApiBinding. */
export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  displaySettingsLinks: !!state.auth.isMember,
}))(SideBarNavLinks);

/** Pass it the links we get from GlobalNavApiBinding */
export const SideBarNavLinksBinding = GlobalNavApiBinding(
  connect<Props, ExternalProps, _, _, _, _>(state => ({
    displaySettingsLinks: !!state.auth.isMember,
  }))(SideBarNavLinks),
);
